import { useEffect, useRef, useCallback } from 'react';
import { useUploadQueueStore, canStartUpload } from '../store/useUploadQueueStore';
import type { UploadQueueItem } from '../types';
import {
  MAX_CONCURRENT,
  MAX_LARGE_CONCURRENT,
  MAX_RETRIES,
  calculateRetryDelay,
  isRetryableError,
  getErrorMessage,
} from '../constants';
import { uploadFileToS3 } from '@/api/client';

interface UseUploadQueueProcessorOptions {
  /** Called when an upload completes successfully */
  onComplete?: (item: UploadQueueItem) => void;
  /** Called when an upload fails after all retries */
  onError?: (item: UploadQueueItem, error: Error) => void;
}

/**
 * Upload Queue Processor Hook
 *
 * This hook should be used once at the application level (e.g., in AppShell)
 * to process the upload queue. It handles:
 * - Processing pending items (smallest-first)
 * - Dynamic concurrency (5 total, max 2 large)
 * - Network-aware pausing
 * - Retry with exponential backoff
 * - S3 upload with progress tracking
 * - Running onComplete callbacks
 */
export function useUploadQueueProcessor(options: UseUploadQueueProcessorOptions = {}) {
  const { onComplete, onError } = options;

  const {
    updateItem,
    setAbortController,
    removeAbortController,
    setIsPaused,
    getNextPendingItem,
    getUploadingItems,
  } = useUploadQueueStore();

  // Track active processing to prevent duplicate processing
  const processingRef = useRef(new Set<string>());
  const isProcessingRef = useRef(false);

  /**
   * Process a single queue item
   */
  const processItem = useCallback(
    async (item: UploadQueueItem) => {
      if (processingRef.current.has(item.id)) {
        return;
      }

      processingRef.current.add(item.id);
      const abortController = new AbortController();
      setAbortController(item.id, abortController);

      try {
        // Audit L2: if a previous attempt already uploaded to S3 but its
        // onComplete callback failed, the fileKey is persisted on the item —
        // skip straight to onComplete instead of re-uploading the bytes.
        let fileKey = item.fileKey;

        if (fileKey === undefined) {
          if (!item.file) {
            throw new Error('File not available');
          }

          // Update status to uploading
          updateItem(item.id, { status: 'uploading', progress: 0 });

          // Upload to S3 (audit M5b: single hardened implementation — zod
          // presign validation + browser-managed multipart boundary live in
          // the API client, not re-implemented here)
          fileKey = await uploadFileToS3(item.file, {
            signal: abortController.signal,
            onProgress: (progress) => updateItem(item.id, { progress }),
            // #84: the S3 leg reports its own failures (it is the one place
            // both upload paths share); it just can't know which attempt it
            // is on. Hand it the count so the report carries it.
            attempt: item.retryCount + 1,
          });

          // Update status to uploaded (persists fileKey for L2 retries)
          updateItem(item.id, { status: 'uploaded', fileKey, progress: 100 });
        } else {
          updateItem(item.id, { status: 'uploaded', progress: 100 });
        }

        // Run onComplete callback if provided
        if (item.onComplete) {
          updateItem(item.id, { status: 'processing' });
          await item.onComplete(fileKey, item);
        }

        // Mark as completed
        updateItem(item.id, { status: 'completed' });
        onComplete?.(item);
      } catch (error) {
        // Handle abort (user cancelled via AbortController)
        // Check for both DOMException AbortError and axios CanceledError
        const isAbortError =
          (error instanceof DOMException && error.name === 'AbortError') ||
          (error && typeof error === 'object' && 'code' in error && error.code === 'ERR_CANCELED');

        if (isAbortError) {
          // Item was cancelled by user or by offline handler, don't update status
          // (offline handler sets status to 'paused' separately)
          return;
        }

        const currentItem = useUploadQueueStore.getState().items.find((i) => i.id === item.id);
        if (!currentItem) return;

        // Check if we should retry
        if (isRetryableError(error) && currentItem.retryCount < MAX_RETRIES) {
          const retryCount = currentItem.retryCount + 1;
          const delay = calculateRetryDelay(retryCount);

          // Audit H3: retryAfter gates getNextPendingItem so the item
          // cannot be re-dispatched before the backoff elapses.
          updateItem(item.id, {
            status: 'pending',
            retryCount,
            progress: 0,
            retryAfter: Date.now() + delay,
            error: `Retry ${retryCount}/${MAX_RETRIES} in ${delay / 1000}s`,
          });

          // Schedule the retry: clearing retryAfter mutates `items`, which
          // fires the store subscription and re-runs processQueue — this
          // timer is both the message cleanup AND the retry trigger.
          setTimeout(() => {
            updateItem(item.id, { error: undefined, retryAfter: undefined });
          }, delay);
        } else {
          // Max retries exceeded or non-retryable error
          const errorMessage = getErrorMessage(error);

          updateItem(item.id, {
            status: 'error',
            error: errorMessage,
            progress: 0,
          });

          onError?.(item, error instanceof Error ? error : new Error(errorMessage));
        }
      } finally {
        processingRef.current.delete(item.id);
        removeAbortController(item.id);
      }
    },
    [updateItem, setAbortController, removeAbortController, onComplete, onError]
  );

  /**
   * Process the queue - pick next items to upload
   * Loop continues until no pending items or concurrency limit is reached
   */
  const processQueue = useCallback(() => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      // Keep starting uploads until we hit concurrency limits or run out of pending items
      while (true) {
        const nextItem = getNextPendingItem();
        if (!nextItem) break;

        // Check concurrency limits
        const currentUploading = getUploadingItems();

        if (!canStartUpload(currentUploading, nextItem, MAX_CONCURRENT, MAX_LARGE_CONCURRENT)) {
          break;
        }

        // Start processing this item
        void processItem(nextItem);
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [getNextPendingItem, getUploadingItems, processItem]);

  /**
   * Handle network status changes
   */
  useEffect(() => {
    const handleOnline = () => {
      setIsPaused(false);
      // Resume any paused items
      const pausedItems = useUploadQueueStore.getState().items.filter(
        (i) => i.status === 'paused'
      );
      pausedItems.forEach((item) => {
        updateItem(item.id, { status: 'pending' });
      });
    };

    const handleOffline = () => {
      setIsPaused(true);
      // Pause any uploading items (they will fail anyway)
      const uploadingItems = useUploadQueueStore.getState().items.filter(
        (i) => i.status === 'uploading'
      );
      uploadingItems.forEach((item) => {
        // Cancel the upload
        const controllers = useUploadQueueStore.getState().abortControllers;
        const controller = controllers.get(item.id);
        if (controller) {
          controller.abort();
        }
        updateItem(item.id, { status: 'paused', progress: 0 });
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      setIsPaused(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setIsPaused, updateItem]);

  /**
   * Process queue when items change
   */
  useEffect(() => {
    // Subscribe to store changes
    const unsubscribe = useUploadQueueStore.subscribe((state, prevState) => {
      // Only process if items changed or isPaused changed
      if (state.items !== prevState.items || state.isPaused !== prevState.isPaused) {
        if (!state.isPaused) {
          // Use setTimeout to avoid processing in the same tick
          setTimeout(processQueue, 0);
        }
      }
    });

    // Initial processing
    if (!useUploadQueueStore.getState().isPaused) {
      processQueue();
    }

    return unsubscribe;
  }, [processQueue]);

  return {
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  };
}
